import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, User, getSetting, updateSetting, getUserPermissions, getAdminWaitlist } from '../services/api';
import UserPermissionsPanel from './Admin/UserPermissionsPanel';
import AuditLogViewer from './Admin/AuditLogViewer';
import CompanyManagement from './Admin/CompanyManagement';
import PlatformConfigPanel from './Admin/PlatformConfigPanel';
import ModelConfigPanel from './Admin/ModelConfigPanel';
import StripeConfigPanel from './Admin/StripeConfigPanel';
import BillingLookupPanel from './Admin/BillingLookupPanel';
import AffiliateAdminPanel from './Admin/AffiliateAdminPanel';
import APIPoolsAdmin from './Admin/APIPoolsAdmin';
import ApiQuotaAdmin from './Admin/ApiQuotaAdmin';
import SupportAdminPanel from './Admin/SupportAdminPanel';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassInput } from './ui/GlassInput';
import { GlassSelect } from './ui/GlassSelect';
import { GlassBadge } from './ui/GlassBadge';
import { GlassTable } from './ui/GlassTable';
import { GlassModal } from './ui/GlassModal';
import { GlassTabs } from './ui/GlassTabs';
import { ModuleHeader } from './ui/ModuleHeader';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Pencil, Trash2, Lock, Unlock } from 'lucide-react';
import './UsersAdmin.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const UsersAdmin: React.FC = () => {
  const { isSuperAdmin } = usePermissions();

  const ADMIN_TABS = [
    { id: 'users', label: 'Users' },
    { id: 'waitlist', label: 'Waitlist' },
    { id: 'companies', label: 'Companies' },
    { id: 'enterprise', label: 'Audit & Stats' },
    { id: 'billing-lookup', label: 'Billing Lookup' },
    { id: 'affiliates', label: 'Affiliates' },
    { id: 'support', label: 'Support' },
    ...(isSuperAdmin ? [{ id: 'platform', label: 'Platform Config' }] : []),
    ...(isSuperAdmin ? [{ id: 'model-config', label: 'Model Config' }] : []),
    ...(isSuperAdmin ? [{ id: 'stripe', label: 'Stripe Billing' }] : []),
    ...(isSuperAdmin ? [{ id: 'api-pools', label: 'API Pools' }] : []),
    ...(isSuperAdmin ? [{ id: 'api-quotas', label: 'API Quotas' }] : []),
  ];
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState<User[]>([]);
  const [waitlist, setWaitlist] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<User | null>(null);
  const [restrictedUserIds, setRestrictedUserIds] = useState<Set<string>>(new Set());
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    is_active: true
  });

  useEffect(() => {
    loadUsers();
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'waitlist') {
      loadWaitlist();
    }
  }, [activeTab]);

  const loadWaitlist = async () => {
    try {
      setIsLoading(true);
      const data = await getAdminWaitlist();
      setWaitlist(data);
    } catch (err: any) {
      setError('Failed to load waitlist');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPermissionIndicators = async (userList: User[]) => {
    const restricted = new Set<string>();
    await Promise.allSettled(
      userList.filter(u => u.role !== 'admin' && u.role !== 'superadmin').map(async u => {
        try {
          const perm = await getUserPermissions(u.user_id);
          if (perm.allowed_modules !== null || perm.allowed_projects !== null) {
            restricted.add(u.user_id);
          }
        } catch {}
      })
    );
    setRestrictedUserIds(restricted);
  };

  const loadSettings = async () => {
    try {
      const setting = await getSetting('registration_enabled');
      setRegistrationEnabled(setting.value.toLowerCase() === 'true');
    } catch (err) {
      setRegistrationEnabled(false);
    }
  };

  const handleToggleRegistration = async () => {
    try {
      setSettingsLoading(true);
      const newValue = !registrationEnabled;
      await updateSetting('registration_enabled', newValue.toString());
      setRegistrationEnabled(newValue);
    } catch (err: any) {
      alert('Failed to update setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getUsers();
      setUsers(data);
      loadPermissionIndicators(data);
    } catch (err: any) {
      setError('Failed to load users. Are you an admin?');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (user: User | null = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        password: '', // Don't show password
        role: user.role,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'user',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      if (editingUser) {
        const updateData: any = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        await updateUser(editingUser.user_id, updateData);
      } else {
        await createUser(formData);
      }
      setShowModal(false);
      loadUsers();
    } catch (err: any) {
      alert(getApiError(err, 'Failed to save user'));
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      loadUsers();
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
    ...(isSuperAdmin ? [{ value: 'superadmin', label: 'Super Admin' }] : []),
  ];

  const tableColumns = [
    { key: 'username', label: 'Username' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'role',
      label: 'Role',
      render: (value: string) => (
        <GlassBadge variant={value === 'superadmin' ? 'warning' : value === 'admin' ? 'danger' : 'info'}>
          {value}
        </GlassBadge>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value: boolean) => (
        <div className="status-cell">
          <span className={`status-dot ${value ? 'active' : 'inactive'}`} />
          <span>{value ? 'Active' : 'Inactive'}</span>
        </div>
      )
    },
    {
      key: 'permissions',
      label: 'Permissions',
      render: (_: any, row: User) => (
        (row.role === 'admin' || row.role === 'superadmin') ? (
          <span className="permissions-text">Full access</span>
        ) : (
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => setPermissionsUser(row)}
            icon={restrictedUserIds.has(row.user_id) ? <Lock size={14} /> : <Unlock size={14} />}
          >
            {restrictedUserIds.has(row.user_id) ? 'Restricted' : 'Manage'}
          </GlassButton>
        )
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, row: User) => (
        <div className="action-buttons">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => handleOpenModal(row)}
            icon={<Pencil size={16} />}
            title="Edit user"
          />
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.user_id)}
            icon={<Trash2 size={16} />}
            title="Delete user"
          />
        </div>
      )
    }
  ];

  if (isLoading) return (
    <div className="users-admin">
      <div className="admin-loading">
        <GlassCard padding="lg">
          <p>Loading...</p>
        </GlassCard>
      </div>
    </div>
  );

  return (
    <div className="users-admin">
      <ModuleHeader
        title="Admin"
        breadcrumb={['Home', 'Admin']}
        actions={activeTab === 'users' ? [
          <GlassButton
            key="add"
            variant="primary"
            onClick={() => handleOpenModal()}
            icon={<Plus size={18} />}
          >
            Add User
          </GlassButton>
        ] : []}
      />

      <GlassTabs tabs={ADMIN_TABS} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'waitlist' && (
        <div className="users-admin-content">
          <GlassCard variant="default" padding="none" className="table-card">
            <GlassTable
              columns={[
                { key: 'email', label: 'Email' },
                { 
                  key: 'created_at', 
                  label: 'Date Joined',
                  render: (val: string) => new Date(val).toLocaleString()
                },
                { 
                  key: 'status', 
                  label: 'Status',
                  render: (val: string) => (
                    <GlassBadge variant={val === 'pending' ? 'info' : 'success'}>
                      {val}
                    </GlassBadge>
                  )
                },
                { 
                  key: 'is_priority', 
                  label: 'Priority',
                  render: (val: boolean) => val ? (
                    <GlassBadge variant="warning">Priority</GlassBadge>
                  ) : (
                    <span style={{ opacity: 0.5 }}>Standard</span>
                  )
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_: any, row: any) => (
                    <GlassButton 
                      variant="primary" 
                      size="sm"
                      onClick={() => alert(`Promote ${row.email} - Feature coming soon`)}
                    >
                      Promote to User
                    </GlassButton>
                  )
                }
              ]}
              data={waitlist}
              rowKey="email"
              emptyMessage="Waitlist is empty"
            />
          </GlassCard>
        </div>
      )}

      {activeTab === 'platform' && isSuperAdmin && <PlatformConfigPanel />}

      {activeTab === 'model-config' && isSuperAdmin && <ModelConfigPanel />}

      {activeTab === 'stripe' && isSuperAdmin && <StripeConfigPanel />}

      {activeTab === 'api-pools' && isSuperAdmin && <APIPoolsAdmin />}
      {activeTab === 'api-quotas' && isSuperAdmin && <ApiQuotaAdmin />}

      {activeTab === 'billing-lookup' && <BillingLookupPanel />}

      {activeTab === 'affiliates' && <AffiliateAdminPanel />}

      {activeTab === 'support' && <SupportAdminPanel />}

      {activeTab === 'companies' && <CompanyManagement />}

      {activeTab === 'enterprise' && (
        <AuditLogViewer />
      )}

      {activeTab === 'users' && (
        <div className="users-admin-content" style={{ display: 'flex', gap: '1.5rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Settings Section */}
            <GlassCard variant="default" padding="md" className="settings-card">
              <div className="setting-row">
                <div className="setting-info">
                  <span className="setting-label">Allow User Registration</span>
                  <span className="setting-description">
                    When enabled, new users can create their own accounts from the login page
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={registrationEnabled}
                    onChange={handleToggleRegistration}
                    disabled={settingsLoading}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </GlassCard>

            {error && (
              <GlassCard variant="default" padding="md" className="error-card">
                <div className="admin-error">{error}</div>
              </GlassCard>
            )}

            {/* Users Table */}
            <GlassCard variant="default" padding="none" className="table-card">
              <GlassTable
                columns={tableColumns}
                data={users}
                rowKey="user_id"
                emptyMessage="No users found"
              />
            </GlassCard>
          </div>

          {/* Permissions side panel */}
          {permissionsUser && (
            <UserPermissionsPanel
              user={permissionsUser}
              onClose={() => {
                setPermissionsUser(null);
                loadUsers();
              }}
            />
          )}
        </div>
      )}

      {/* User Modal */}
      <GlassModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Edit User' : 'Add New User'}
        size="md"
        footer={
          <>
            <GlassButton
              variant="ghost"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              type="submit"
              onClick={handleSubmit}
            >
              Save User
            </GlassButton>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="user-form">
          <GlassInput
            label="Username"
            value={formData.username}
            onChange={(value) => setFormData({...formData, username: value})}
            required
          />
          <GlassInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={(value) => setFormData({...formData, email: value})}
            required
          />
          <GlassInput
            label="Full Name"
            value={formData.full_name}
            onChange={(value) => setFormData({...formData, full_name: value})}
          />
          <GlassInput
            label={`Password ${editingUser ? '(Leave blank to keep current)' : ''}`}
            type="password"
            value={formData.password}
            onChange={(value) => setFormData({...formData, password: value})}
            required={!editingUser}
          />
          <GlassSelect
            label="Role"
            options={roleOptions}
            value={formData.role}
            onChange={(value) => setFormData({...formData, role: value})}
          />
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
            />
            <span>Active Account</span>
          </label>
        </form>
      </GlassModal>
    </div>
  );
};

export default UsersAdmin;
